/**
 * ClientPopupRenderer — mounts on ClientPage.
 * For client-role users, polls /clients/:id/popups and displays the newest
 * un-dismissed popup as a modal overlay. On dismiss, POSTs to the dismiss
 * endpoint (which may freeze the engagement).
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { AlertTriangle, Info, ShieldAlert, X } from "lucide-react";
import { useAuth } from "../lib/auth";

const TONE_STYLE = {
  info:    { icon: Info,          border: "border-sky-800",   tag: "bg-sky-950 text-sky-300" },
  warning: { icon: AlertTriangle, border: "border-amber-800", tag: "bg-amber-950 text-amber-300" },
  danger:  { icon: ShieldAlert,   border: "border-red-800",   tag: "bg-red-950 text-red-300" },
};

export default function ClientPopupRenderer({ clientRef, onFrozen }) {
  const { user } = useAuth();
  const [popups, setPopups] = useState([]);
  const [busy, setBusy] = useState(false);

  const fetchPopups = useCallback(async () => {
    if (!clientRef || user?.role !== "client") return;
    try {
      const r = await api.get(`/clients/${clientRef}/popups`);
      setPopups(r.data || []);
    } catch { /* silent */ }
  }, [clientRef, user?.role]);

  useEffect(() => {
    fetchPopups();
    if (user?.role !== "client") return undefined;
    const t = setInterval(fetchPopups, 30000);
    return () => clearInterval(t);
  }, [fetchPopups, user?.role]);

  // Track view (read receipt) — one hit per popup id per session
  const currentId = popups[0]?.id;
  useEffect(() => {
    if (!currentId || !clientRef || user?.role !== "client") return;
    api.post(`/clients/${clientRef}/popups/${currentId}/view`).catch(() => {});
  }, [currentId, clientRef, user?.role]);

  if (user?.role !== "client" || !popups.length) return null;

  const current = popups[0]; // newest first (backend sorts desc)
  const tone = TONE_STYLE[current.tone] || TONE_STYLE.info;
  const Icon = tone.icon;

  const dismiss = async () => {
    if (!current.dismissible) return;
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientRef}/popups/${current.id}/dismiss`);
      setPopups((prev) => prev.filter((p) => p.id !== current.id));
      if (r.data?.froze && onFrozen) onFrozen();
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="client-popup-overlay">
      <div className={`bg-[#0A0A0A] border ${tone.border} rounded-lg shadow-2xl max-w-md w-full p-6 anim-fade-up relative`}
        data-testid={`client-popup-${current.id}`}>
        {current.dismissible && (
          <button onClick={dismiss} disabled={busy}
            className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-200 transition-colors"
            data-testid="client-popup-close">
            <X className="w-4 h-4" />
          </button>
        )}
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] font-mono ${tone.tag}`}>
          <Icon className="w-3 h-3" /> {current.tone || "info"}
        </div>
        <h2 className="text-zinc-100 text-[18px] font-display font-semibold mt-3 leading-tight" data-testid="client-popup-title">
          {current.title}
        </h2>
        <p className="text-zinc-300 text-[13px] leading-relaxed mt-2 whitespace-pre-line" data-testid="client-popup-body">
          {current.body}
        </p>
        {current.is_freeze && (
          <div className="mt-3 text-[11px] text-red-300 bg-red-950/40 border border-red-900 rounded px-2 py-1.5">
            Dismissing this notice will pause dashboard access pending resolution.
          </div>
        )}
        <div className="flex justify-end mt-5">
          {current.dismissible ? (
            <button onClick={dismiss} disabled={busy}
              className="bg-white text-black text-[12px] font-medium px-4 h-9 rounded hover:bg-zinc-200 disabled:opacity-50"
              data-testid="client-popup-dismiss">
              {busy ? "…" : (current.is_freeze ? "Acknowledge & pause" : "Got it")}
            </button>
          ) : (
            <div className="text-[11px] text-zinc-500 font-mono">
              This message can't be dismissed. Contact your account manager.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
